import {
  LadderKind,
  LadderLevel,
  LevelsLadderModel,
  sortEntries,
  sortTakeProfits,
} from "common/NewLook/priceLadder.ts";

export type TradeBarLane = "single" | "top" | "bottom";
export type TradeBarTickLane = TradeBarLane | "full";

export interface TradeBarTick {
  key: string;
  x: number;
  tone: LadderKind;
  lane: TradeBarTickLane;
  isSplit?: boolean;
}

export interface TradeBarBand {
  key: string;
  from: number;
  to: number;
  tone: LadderKind;
  isSolid: boolean;
  lane: TradeBarLane;
}

export interface TradeBarLabelPart {
  text: string;
  tone?: LadderKind;
  isMuted?: boolean;
}

export interface TradeBarLabelLine {
  key: string;
  parts: TradeBarLabelPart[];
  left: number;
  row: number;
}

export interface TradeBarTexts {
  entry: string;
  takeProfit: string;
  stop: string;
  noStop: string;
  noTakeProfit: string;
}

export interface TradeBarOptions {
  width: number;
  formatPrice: (price: number) => string;
  measure: (part: TradeBarLabelPart) => number;
  texts: TradeBarTexts;
  showLabels: boolean;
}

export interface TradeBarPoints {
  entries: number[];
  takeProfits: number[];
  stop?: number;
  price?: number;
}

export interface TradeBarLayout {
  hasLanes: boolean;
  isStopOffScale: boolean;
  isTakeProfitOffScale: boolean;
  bands: TradeBarBand[];
  ticks: TradeBarTick[];
  markerX?: number;
  markerRing?: LadderKind;
  isClosed: boolean;
  lines: TradeBarLabelLine[];
  rows: number;
  points: TradeBarPoints;
}

export interface TradeBarOrders {
  isShort: boolean;
  isComplete: boolean;
  isClosed: boolean;
  currentPrice?: number;
  entries: LadderLevel[];
  takeProfits: LadderLevel[];
  stop?: LadderLevel;
  nextEntryIndex: number;
  nextTakeProfitIndex: number;
}

interface TradeBarScale {
  x: (price: number) => number;
  isStopOffScale: boolean;
  inScaleTakeProfits: number;
}

interface LabelSpec {
  key: string;
  x: number;
  priority: number;
  variants: TradeBarLabelPart[][][];
}

interface PlacedLine {
  left: number;
  right: number;
  row: number;
}

interface LabelState {
  spec: LabelSpec;
  lines: TradeBarLabelPart[][];
  first?: TradeBarLabelLine;
}

const OFF_SCALE_RATIO = 1.5;
const OFF_SCALE_STOP_START = 0.25;
const NO_STOP_START = 0.06;
const OFF_SCALE_TAKE_PROFIT_END = 0.85;
const ARROW_WIDTH = 9;
const TICK_GAP = 6;
const RING_DISTANCE = 8;
const LABEL_GAP = 10;
const PART_GAP = 3;
const PRIORITY = { stop: 0, takeProfit: 1, entry: 2 };

export const getTradeBarPrice = (model: LevelsLadderModel) =>
  model.closePrice ?? model.currentPrice;

export const isTradeBarModel = (model: LevelsLadderModel) => {
  if (model.isClosed) return model.levels.length > 0;
  const price = getTradeBarPrice(model);
  return !!price && price > 0;
};

const lastFilledIndex = (levels: LadderLevel[]) =>
  levels.reduce((last, level, index) => (level.filled ? index : last), -1);

const getNextTakeProfitIndex = (
  takeProfits: LadderLevel[],
  isClosed: boolean
) => {
  if (isClosed) return Math.max(lastFilledIndex(takeProfits), 0);
  const next = takeProfits.findIndex((level) => !level.filled);
  return next < 0 ? takeProfits.length - 1 : next;
};

export const getTradeBarOrders = (model: LevelsLadderModel): TradeBarOrders => {
  const ofKind = (kind: LadderKind) =>
    model.levels.filter((level) => level.kind === kind && level.price > 0);
  const entries = sortEntries(ofKind("entry"), model.isShort);
  const takeProfits = sortTakeProfits(ofKind("take_profit"), model.isShort);
  const isClosed = !!model.isClosed;
  return {
    isShort: model.isShort,
    isComplete: model.isComplete,
    isClosed,
    currentPrice: getTradeBarPrice(model),
    entries,
    takeProfits,
    stop: ofKind("stop")[0],
    nextEntryIndex: isClosed ? -1 : entries.findIndex((level) => !level.filled),
    nextTakeProfitIndex: getNextTakeProfitIndex(takeProfits, isClosed),
  };
};

const getSpan = (values: number[]) => {
  const span = Math.max(...values) - Math.min(...values);
  return span > 0 ? span : Math.abs(values[0]) * 0.01 || 1;
};

const distanceOutside = (value: number, values: number[]) =>
  Math.max(Math.min(...values) - value, value - Math.max(...values), 0);

const getCoreValues = (
  orders: TradeBarOrders,
  toValue: (price: number) => number
) => {
  const nextTakeProfit = orders.takeProfits[orders.nextTakeProfitIndex];
  const values = [
    ...orders.entries.map((level) => toValue(level.price)),
    ...(orders.currentPrice ? [toValue(orders.currentPrice)] : []),
    ...(nextTakeProfit ? [toValue(nextTakeProfit.price)] : []),
  ];
  return values.length || !orders.stop ? values : [toValue(orders.stop.price)];
};

const countInScaleTakeProfits = (
  orders: TradeBarOrders,
  values: number[],
  toValue: (price: number) => number
) => {
  const span = getSpan(values);
  const inScale = orders.takeProfits.findIndex(
    (level, index) =>
      index > orders.nextTakeProfitIndex &&
      distanceOutside(toValue(level.price), values) > span * OFF_SCALE_RATIO
  );
  return inScale < 0 ? orders.takeProfits.length : inScale;
};

const getScale = (orders: TradeBarOrders, width: number): TradeBarScale => {
  const toValue = (price: number) => (orders.isShort ? -price : price);
  const values = getCoreValues(orders, toValue);
  const stopValue = orders.stop ? toValue(orders.stop.price) : undefined;
  const isStopOffScale =
    stopValue !== undefined &&
    distanceOutside(stopValue, values) > getSpan(values) * OFF_SCALE_RATIO;
  if (stopValue !== undefined && !isStopOffScale) values.push(stopValue);
  const inScaleTakeProfits = countInScaleTakeProfits(orders, values, toValue);
  orders.takeProfits
    .slice(0, inScaleTakeProfits)
    .forEach((level) => values.push(toValue(level.price)));
  const low = Math.min(...values);
  const span = getSpan(values);
  const start = isStopOffScale
    ? OFF_SCALE_STOP_START
    : orders.stop
    ? 0
    : NO_STOP_START;
  const end =
    inScaleTakeProfits < orders.takeProfits.length
      ? OFF_SCALE_TAKE_PROFIT_END
      : 1;
  const x = (price: number) =>
    (start + ((toValue(price) - low) / span) * (end - start)) * width;
  return { x, isStopOffScale, inScaleTakeProfits };
};

const getRange = (levels: LadderLevel[]) => {
  const prices = levels.map((level) => level.price);
  return { low: Math.min(...prices), high: Math.max(...prices) };
};

const hasOverlappingZones = (orders: TradeBarOrders) => {
  if (orders.entries.length < 2 || orders.takeProfits.length < 2) return false;
  const entries = getRange(orders.entries);
  const takeProfits = getRange(orders.takeProfits);
  return entries.low <= takeProfits.high && takeProfits.low <= entries.high;
};

const getZoneBands = (
  key: string,
  levels: LadderLevel[],
  tone: LadderKind,
  lane: TradeBarLane,
  to: (level: LadderLevel) => number,
  x: (price: number) => number
): TradeBarBand[] => {
  if (levels.length < 2) return [];
  const filled = levels.filter((level) => level.filled > 0);
  const zone = {
    key,
    from: x(levels[0].price),
    to: to(levels[levels.length - 1]),
    tone,
    lane,
  };
  const bands: TradeBarBand[] = [{ ...zone, isSolid: false }];
  if (filled.length > 1)
    bands.push({
      ...zone,
      key: `${key}-filled`,
      to: x(filled[filled.length - 1].price),
      isSolid: true,
    });
  return bands;
};

const getBands = (
  orders: TradeBarOrders,
  scale: TradeBarScale,
  hasLanes: boolean,
  width: number
) => {
  const takeProfitEnd = (level: LadderLevel) =>
    scale.inScaleTakeProfits < orders.takeProfits.length
      ? width - ARROW_WIDTH
      : scale.x(level.price);
  return [
    ...getZoneBands(
      "entries",
      orders.entries,
      "entry",
      hasLanes ? "bottom" : "single",
      (level) => scale.x(level.price),
      scale.x
    ),
    ...getZoneBands(
      "take-profits",
      orders.takeProfits,
      "take_profit",
      hasLanes ? "top" : "single",
      takeProfitEnd,
      scale.x
    ),
  ];
};

const isSplitStop = (orders: TradeBarOrders) =>
  !!orders.stop &&
  orders.entries.length === 1 &&
  orders.entries[0].price === orders.stop.price;

const getEntryMarkerIndex = (orders: TradeBarOrders) => {
  if (orders.isClosed) return Math.max(lastFilledIndex(orders.entries), 0);
  return orders.nextEntryIndex < 0
    ? orders.entries.length - 1
    : orders.nextEntryIndex;
};

const getMarkerOrders = (orders: TradeBarOrders, scale: TradeBarScale) => {
  const markers: { key: string; price: number; tone: LadderKind }[] = [];
  if (orders.stop && !scale.isStopOffScale)
    markers.push({ key: "stop", price: orders.stop.price, tone: "stop" });
  const nextEntry = orders.entries[getEntryMarkerIndex(orders)];
  if (nextEntry && !isSplitStop(orders))
    markers.push({ key: "entry", price: nextEntry.price, tone: "entry" });
  const nextTakeProfit = orders.takeProfits[orders.nextTakeProfitIndex];
  if (nextTakeProfit)
    markers.push({
      key: "take-profit",
      price: nextTakeProfit.price,
      tone: "take_profit",
    });
  return markers;
};

const spreadPositions = (positions: number[], width: number) => {
  const spread = [...positions];
  for (let index = 1; index < spread.length; index++)
    spread[index] = Math.max(spread[index], spread[index - 1] + TICK_GAP);
  for (let index = spread.length - 1; index >= 0; index--)
    spread[index] = Math.min(
      spread[index],
      index < spread.length - 1 ? spread[index + 1] - TICK_GAP : width
    );
  for (let index = 0; index < spread.length; index++)
    spread[index] = Math.max(
      spread[index],
      index ? spread[index - 1] + TICK_GAP : 0
    );
  return spread;
};

const tickLane = (tone: LadderKind, hasLanes: boolean): TradeBarTickLane => {
  if (!hasLanes) return "single";
  if (tone === "stop") return "full";
  return tone === "entry" ? "bottom" : "top";
};

const getTicks = (
  orders: TradeBarOrders,
  scale: TradeBarScale,
  hasLanes: boolean,
  width: number
): TradeBarTick[] => {
  const markers = getMarkerOrders(orders, scale)
    .map((marker) => ({ ...marker, x: scale.x(marker.price) }))
    .sort((first, second) => first.x - second.x);
  const positions = spreadPositions(
    markers.map((marker) => marker.x),
    width
  );
  return markers.map((marker, index) => ({
    key: marker.key,
    x: positions[index],
    tone: marker.tone,
    lane: tickLane(marker.tone, hasLanes),
    isSplit: marker.tone === "stop" && isSplitStop(orders),
  }));
};

const getMarkerRing = (ticks: TradeBarTick[], markerX: number) =>
  ticks
    .filter((tick) => Math.abs(tick.x - markerX) < RING_DISTANCE)
    .sort(
      (first, second) =>
        Math.abs(first.x - markerX) - Math.abs(second.x - markerX)
    )[0]?.tone;

export const formatTradeBarChange = (
  price: number,
  base: number,
  isShort: boolean
) => {
  const change = (price / base - 1) * 100;
  const outcome = isShort ? -change : change;
  const size = Math.abs(outcome);
  return `${outcome >= 0 ? "+" : "−"}${
    size >= 100 ? size.toFixed(0) : size.toFixed(1)
  }%`;
};

const formatPercent = (price: number, orders: TradeBarOrders) =>
  formatTradeBarChange(price, orders.currentPrice as number, orders.isShort);

const muted = (text: string): TradeBarLabelPart => ({ text, isMuted: true });

const rangePart = (
  levels: LadderLevel[],
  formatPrice: (price: number) => string
) => {
  const { low, high } = getRange(levels);
  return muted(`${formatPrice(low)} – ${formatPrice(high)}`);
};

const countText = (orders: TradeBarOrders, index: number, total: number) =>
  orders.isComplete && total > 1 ? `${index + 1}/${total}` : "";

type LabelLines = TradeBarLabelPart[][];

const percentVariants = (
  head: TradeBarLabelPart[],
  rest: LabelLines,
  percent?: TradeBarLabelPart,
  keywordCount?: number
): LabelLines[] => {
  const keywords = keywordCount ? head.slice(0, keywordCount) : [];
  const priceParts = keywordCount ? head.slice(keywordCount) : [];
  if (!percent)
    return [[head, ...rest], ...(keywordCount ? [[keywords, priceParts]] : [])];
  return [
    [[...head, percent], ...rest],
    [head, [percent]],
    ...(keywordCount ? [[keywords, [...priceParts, percent]]] : []),
  ];
};

const singleLine = (part: TradeBarLabelPart): LabelLines[] => [[[part]]];

const getStopLabel = (
  orders: TradeBarOrders,
  x: number,
  options: TradeBarOptions
): LabelSpec => {
  const { texts, formatPrice } = options;
  if (!orders.stop)
    return {
      key: "stop",
      x: 0,
      priority: PRIORITY.stop,
      variants: singleLine(muted(texts.noStop)),
    };
  const keywords: TradeBarLabelPart[] = [{ text: texts.stop, tone: "stop" }];
  if (isSplitStop(orders)) keywords.push({ text: texts.entry, tone: "entry" });
  const percent = orders.isClosed
    ? undefined
    : muted(formatPercent(orders.stop.price, orders));
  return {
    key: "stop",
    x,
    priority: PRIORITY.stop,
    variants: percentVariants(
      [...keywords, { text: formatPrice(orders.stop.price) }],
      [],
      percent
    ),
  };
};

const getTakeProfitLabel = (
  orders: TradeBarOrders,
  x: number,
  options: TradeBarOptions
): LabelSpec => {
  const { texts, formatPrice } = options;
  const total = orders.takeProfits.length;
  const level = orders.takeProfits[orders.nextTakeProfitIndex];
  if (!level)
    return {
      key: "take-profit",
      x: options.width,
      priority: PRIORITY.takeProfit,
      variants: singleLine(muted(texts.noTakeProfit)),
    };
  if (orders.isClosed)
    return getClosedLabel(
      "take-profit",
      orders.takeProfits,
      x,
      texts.takeProfit,
      "take_profit",
      PRIORITY.takeProfit,
      formatPrice
    );
  const keyword = {
    text: `${texts.takeProfit}${countText(
      orders,
      orders.nextTakeProfitIndex,
      total
    )}`,
    tone: "take_profit" as const,
  };
  const range = total > 1 ? [[rangePart(orders.takeProfits, formatPrice)]] : [];
  return {
    key: "take-profit",
    x,
    priority: PRIORITY.takeProfit,
    variants: percentVariants(
      [keyword, { text: formatPrice(level.price) }],
      range,
      muted(formatPercent(level.price, orders)),
      total > 1 ? 1 : undefined
    ),
  };
};

const getClosedLabel = (
  key: string,
  levels: LadderLevel[],
  x: number,
  keyword: string,
  tone: LadderKind,
  priority: number,
  formatPrice: (price: number) => string
): LabelSpec => {
  const filled = levels.filter((level) => level.filled).length;
  const variants: LabelLines[] =
    levels.length === 1
      ? [
          [
            [
              { text: keyword.trim(), tone },
              { text: formatPrice(levels[0].price) },
            ],
          ],
        ]
      : [
          [
            [{ text: `${keyword}${filled}/${levels.length}`, tone }],
            [rangePart(levels, formatPrice)],
          ],
        ];
  return { key, x, priority, variants };
};

const getEntryLabel = (
  orders: TradeBarOrders,
  x: number,
  options: TradeBarOptions
): LabelSpec | undefined => {
  if (!orders.entries.length || isSplitStop(orders)) return;
  if (orders.isClosed)
    return getClosedLabel(
      "entry",
      orders.entries,
      x,
      `${options.texts.entry} `,
      "entry",
      PRIORITY.entry,
      options.formatPrice
    );
  return getOpenEntryLabel(orders, x, options);
};

const getOpenEntryLabel = (
  orders: TradeBarOrders,
  x: number,
  options: TradeBarOptions
): LabelSpec => {
  const { texts, formatPrice } = options;
  const total = orders.entries.length;
  const index = orders.nextEntryIndex < 0 ? total - 1 : orders.nextEntryIndex;
  const isAllFilled = orders.nextEntryIndex < 0 && total > 1;
  const keyword = {
    text: `${texts.entry} ${
      isAllFilled ? `${total}/${total}` : countText(orders, index, total)
    }`.trim(),
    tone: "entry" as const,
  };
  const range = total > 1 ? [[rangePart(orders.entries, formatPrice)]] : [];
  if (isAllFilled)
    return {
      key: "entry",
      x,
      priority: PRIORITY.entry,
      variants: [[[keyword], ...range]],
    };
  const level = orders.entries[index];
  return {
    key: "entry",
    x,
    priority: PRIORITY.entry,
    variants: percentVariants(
      [keyword, { text: formatPrice(level.price) }],
      range,
      level.filled ? undefined : muted(formatPercent(level.price, orders)),
      total > 1 ? 1 : undefined
    ),
  };
};

const getLabelSpecs = (
  orders: TradeBarOrders,
  ticks: TradeBarTick[],
  scale: TradeBarScale,
  options: TradeBarOptions
) => {
  const tickX = (key: string, fallback: number) =>
    ticks.find((tick) => tick.key === key)?.x ?? fallback;
  const entryZone = orders.entries.length
    ? getRange(orders.entries)
    : undefined;
  const entryX = entryZone
    ? (scale.x(entryZone.low) + scale.x(entryZone.high)) / 2
    : 0;
  return [
    getStopLabel(orders, scale.isStopOffScale ? 0 : tickX("stop", 0), options),
    getTakeProfitLabel(orders, tickX("take-profit", options.width), options),
    getEntryLabel(orders, tickX("entry", entryX), options),
  ].filter((spec): spec is LabelSpec => !!spec);
};

const lineWidth = (
  parts: TradeBarLabelPart[],
  measure: (part: TradeBarLabelPart) => number
) =>
  parts.reduce((sum, part) => sum + measure(part), 0) +
  PART_GAP * (parts.length - 1);

const clampLeft = (left: number, lineSize: number, width: number) =>
  Math.max(0, Math.min(width - lineSize, left));

const packFirstLines = (
  states: LabelState[],
  options: TradeBarOptions
): number[] | undefined => {
  const sorted = [...states].sort(
    (first, second) =>
      first.spec.x - second.spec.x || first.spec.priority - second.spec.priority
  );
  const sizes = sorted.map((state) =>
    lineWidth(state.lines[0], options.measure)
  );
  const lefts: number[] = [];
  sorted.forEach((state, index) => {
    const desired = clampLeft(
      state.spec.x - sizes[index] / 2,
      sizes[index],
      options.width
    );
    lefts.push(
      index
        ? Math.max(desired, lefts[index - 1] + sizes[index - 1] + LABEL_GAP)
        : desired
    );
  });
  for (let index = sorted.length - 1; index >= 0; index--) {
    const limit =
      index < sorted.length - 1 ? lefts[index + 1] - LABEL_GAP : options.width;
    lefts[index] = Math.min(lefts[index], limit - sizes[index]);
  }
  if (lefts.some((left) => left < 0)) return;
  return states.map((state) => lefts[sorted.indexOf(state)]);
};

const DEGRADE_ORDER = [PRIORITY.entry, PRIORITY.takeProfit, PRIORITY.stop];

const useVariant = (row: LabelState[], priority: number, level: number) =>
  row
    .filter(
      (state) => state.spec.priority === priority && state.spec.variants[level]
    )
    .forEach((state) => (state.lines = state.spec.variants[level]));

const fitFirstRow = (states: LabelState[], options: TradeBarOptions) => {
  let row = [...states];
  const levels = Math.max(...states.map((state) => state.spec.variants.length));
  for (let level = 1; level < levels; level++)
    for (const priority of DEGRADE_ORDER) {
      if (packFirstLines(row, options)) return row;
      useVariant(row, priority, level);
    }
  while (row.length && !packFirstLines(row, options)) {
    const dropped = row.reduce((most, state) =>
      state.spec.priority > most.spec.priority ? state : most
    );
    row = row.filter((state) => state !== dropped);
  }
  return row;
};

const fits = (placed: PlacedLine[], left: number, size: number, row: number) =>
  placed.every(
    (line) =>
      line.row !== row ||
      left >= line.right + LABEL_GAP ||
      left + size + LABEL_GAP <= line.left
  );

const findLeft = (
  placed: PlacedLine[],
  desired: number,
  size: number,
  row: number,
  width: number
) => {
  const left = clampLeft(desired, size, width);
  if (fits(placed, left, size, row)) return left;
  return placed
    .filter((line) => line.row === row)
    .flatMap((line) => [line.left - LABEL_GAP - size, line.right + LABEL_GAP])
    .map((candidate) => clampLeft(candidate, size, width))
    .filter((candidate) => fits(placed, candidate, size, row))
    .sort(
      (first, second) => Math.abs(first - desired) - Math.abs(second - desired)
    )[0];
};

const placeLine = (
  placed: PlacedLine[],
  key: string,
  parts: TradeBarLabelPart[],
  desired: number,
  minRow: number,
  options: TradeBarOptions
): TradeBarLabelLine => {
  const size = lineWidth(parts, options.measure);
  for (let row = minRow; ; row++) {
    const left = findLeft(placed, desired, size, row, options.width);
    if (left === undefined) continue;
    placed.push({ left, right: left + size, row });
    return { key, parts, left, row };
  }
};

const alignUnder = (
  first: TradeBarLabelLine,
  parts: TradeBarLabelPart[],
  side: "start" | "middle" | "end",
  options: TradeBarOptions
) => {
  const firstSize = lineWidth(first.parts, options.measure);
  const size = lineWidth(parts, options.measure);
  if (side === "start") return first.left;
  if (side === "end") return first.left + firstSize - size;
  return first.left + firstSize / 2 - size / 2;
};

const placeSubLines = (
  states: LabelState[],
  placed: PlacedLine[],
  lines: TradeBarLabelLine[],
  options: TradeBarOptions
) => {
  const ordered = [...states].sort(
    (first, second) => (first.first?.left ?? 0) - (second.first?.left ?? 0)
  );
  const side = (state: LabelState) =>
    ordered.indexOf(state) === 0
      ? "start"
      : ordered.indexOf(state) === ordered.length - 1
      ? "end"
      : "middle";
  const depth = Math.max(...states.map((state) => state.lines.length));
  for (let level = 1; level < depth; level++)
    states.forEach((state) => {
      const parts = state.lines[level];
      if (!parts || !state.first) return;
      const previous = lines
        .filter((line) => line.key.startsWith(state.spec.key))
        .pop() as TradeBarLabelLine;
      const desired = alignUnder(state.first, parts, side(state), options);
      lines.push(
        placeLine(
          placed,
          `${state.spec.key}-${level}`,
          parts,
          desired,
          previous.row + 1,
          options
        )
      );
    });
};

const placeOverflow = (
  states: LabelState[],
  placed: PlacedLine[],
  lines: TradeBarLabelLine[],
  options: TradeBarOptions
) =>
  states.forEach((state) => {
    const size = lineWidth(state.lines[0], options.measure);
    let previous = placeLine(
      placed,
      `${state.spec.key}-0`,
      state.lines[0],
      state.spec.x - size / 2,
      1,
      options
    );
    lines.push(previous);
    state.lines.slice(1).forEach((parts, index) => {
      previous = placeLine(
        placed,
        `${state.spec.key}-${index + 1}`,
        parts,
        alignUnder(lines[lines.length - 1], parts, "middle", options),
        previous.row + 1,
        options
      );
      lines.push(previous);
    });
  });

const layoutLabels = (specs: LabelSpec[], options: TradeBarOptions) => {
  const states: LabelState[] = specs.map((spec) => ({
    spec,
    lines: spec.variants[0],
  }));
  const row = fitFirstRow(states, options);
  const lefts = packFirstLines(row, options) ?? [];
  const placed: PlacedLine[] = [];
  const lines: TradeBarLabelLine[] = [];
  row.forEach((state, index) => {
    const size = lineWidth(state.lines[0], options.measure);
    state.first = {
      key: `${state.spec.key}-0`,
      parts: state.lines[0],
      left: lefts[index],
      row: 0,
    };
    placed.push({ left: lefts[index], right: lefts[index] + size, row: 0 });
    lines.push(state.first);
  });
  placeSubLines(row, placed, lines, options);
  placeOverflow(
    states.filter((state) => !row.includes(state)),
    placed,
    lines,
    options
  );
  return lines;
};

const tickPosition = (ticks: TradeBarTick[], key: string, fallback: number) =>
  ticks.find((tick) => tick.key === key)?.x ?? fallback;

const getTakeProfitPoints = (
  orders: TradeBarOrders,
  scale: TradeBarScale,
  ticks: TradeBarTick[],
  width: number
) =>
  orders.takeProfits.map((level, index) => {
    if (index >= scale.inScaleTakeProfits) return width - ARROW_WIDTH / 2;
    const x = scale.x(level.price);
    return index === orders.nextTakeProfitIndex
      ? tickPosition(ticks, "take-profit", x)
      : x;
  });

const getStopPoint = (
  orders: TradeBarOrders,
  scale: TradeBarScale,
  ticks: TradeBarTick[]
) => {
  if (!orders.stop) return;
  if (scale.isStopOffScale) return ARROW_WIDTH / 2;
  return tickPosition(ticks, "stop", scale.x(orders.stop.price));
};

const getPoints = (
  orders: TradeBarOrders,
  scale: TradeBarScale,
  ticks: TradeBarTick[],
  width: number,
  price?: number
): TradeBarPoints => ({
  entries: orders.entries.map((level) => scale.x(level.price)),
  takeProfits: getTakeProfitPoints(orders, scale, ticks, width),
  stop: getStopPoint(orders, scale, ticks),
  price,
});

export const getTradeBarLayout = (
  model: LevelsLadderModel,
  options: TradeBarOptions
): TradeBarLayout => {
  const orders = getTradeBarOrders(model);
  const scale = getScale(orders, options.width);
  const hasLanes = hasOverlappingZones(orders);
  const ticks = getTicks(orders, scale, hasLanes, options.width);
  const markerX = orders.currentPrice
    ? scale.x(orders.currentPrice)
    : undefined;
  const lines = options.showLabels
    ? layoutLabels(getLabelSpecs(orders, ticks, scale, options), options)
    : [];
  return {
    hasLanes,
    isStopOffScale: scale.isStopOffScale,
    isTakeProfitOffScale: scale.inScaleTakeProfits < orders.takeProfits.length,
    bands: getBands(orders, scale, hasLanes, options.width),
    ticks,
    markerX,
    markerRing:
      orders.isClosed || markerX === undefined
        ? undefined
        : getMarkerRing(ticks, markerX),
    isClosed: orders.isClosed,
    lines,
    rows: lines.reduce((most, line) => Math.max(most, line.row + 1), 0),
    points: getPoints(orders, scale, ticks, options.width, markerX),
  };
};
